import * as React from 'react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, X, Sparkles, ChevronDown, ChevronUp, Store, Loader2, LayoutGrid, List } from 'lucide-react';
import { SourceIcon } from '@/components/products/SourceIcon';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { SavedItem } from '@/hooks/useSavedItems';
import { useSemanticSearch, type SearchMatch } from '@/hooks/useSemanticSearch';
import type { ViewMode } from '@/components/saves/SavesTabNav';

// Smart filter configuration
const categoryIcons: Record<string, string> = {
  shoes: '👟',
  tops: '👕',
  bottoms: '👖',
  outerwear: '🧥',
  dresses: '👗',
  accessories: '⌚',
  bags: '👜',
  underwear: '🧦',
  sportswear: '🏃',
  beauty: '💄',
  tech: '📱',
  home: '🏠',
  books: '📚',
  food: '🍕',
  drinks: '🥤',
  fitness: '💪',
  travel: '✈️',
  entertainment: '🎬',
  toys: '🧸',
  fashion: '👗',
  shopping: '🛍️',
  social: '📲',
  other: '📦',
};

const socialPlatforms = ['instagram', 'tiktok', 'youtube', 'pinterest'];

const detectPlatformFromUrl = (url: string | null): string | null => {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  if (lowerUrl.includes('instagram.com')) return 'instagram';
  if (lowerUrl.includes('tiktok.com')) return 'tiktok';
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) return 'pinterest';
  return null;
};

const sourceConfig: Record<string, { label: string }> = {
  social: { label: 'Social' },
  shopping: { label: 'Shopping Sites' },
  uploads: { label: 'Uploaded Photos' },
  scanned: { label: 'Scanned Items' },
};

interface SmartFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SavedItem[];
  currentFilter: string;
  onFilterChange: (filter: string) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  hideOrganised: boolean;
  onHideOrganisedChange: (value: boolean) => void;
  organisedCount: number;
}

export function SmartFilterSheet({
  open,
  onOpenChange,
  items,
  currentFilter,
  onFilterChange,
  viewMode,
  onViewModeChange,
  hideOrganised,
  onHideOrganisedChange,
  organisedCount,
}: SmartFilterSheetProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [retailersExpanded, setRetailersExpanded] = useState(true);
  const [sourcesExpanded, setSourcesExpanded] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllRetailers, setShowAllRetailers] = useState(false);
  const [matchingItemsExpanded, setMatchingItemsExpanded] = useState(true);
  
  // Semantic search hook
  const { matches, isSearching, error, search, clearSearch } = useSemanticSearch(items);
  
  // Debounced semantic search
  const debounceRef = React.useRef<NodeJS.Timeout>();
  // Track the last query that produced the active search filter
  const lastAppliedQueryRef = React.useRef<string>('');
  
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce semantic search
    if (value.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        search(value);
      }, 400);
    } else {
      clearSearch();
    }
  }, [search, clearSearch]);

  // Restore search query when sheet opens if a semantic search filter is active
  useEffect(() => {
    if (open) {
      if (currentFilter.startsWith('search:') && lastAppliedQueryRef.current) {
        // Restore the previous search query and re-run the search
        setSearchQuery(lastAppliedQueryRef.current);
        search(lastAppliedQueryRef.current);
      } else if (!currentFilter.startsWith('search:')) {
        // Only reset if not in a search filter
        setSearchQuery('');
        clearSearch();
        lastAppliedQueryRef.current = '';
      }
    }
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [open, clearSearch, currentFilter, search]);

  // Group items by AI category
  const autoCategorizedGroups = useMemo(() => {
    return items.reduce((acc, item) => {
      const category = item.ai_category || 'other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {} as Record<string, SavedItem[]>);
  }, [items]);

  // Group items by retailer/store
  const retailerGroups = useMemo(() => {
    return items.reduce((acc, item) => {
      let storeName = item.store;
      
      // Fallback to domain extraction from URL
      if (!storeName && item.url) {
        try {
          const hostname = new URL(item.url).hostname.replace('www.', '');
          storeName = hostname.split('.')[0];
        } catch {
          // Invalid URL, skip
        }
      }
      
      if (storeName) {
        const normalized = storeName.charAt(0).toUpperCase() + storeName.slice(1).toLowerCase();
        if (!acc[normalized]) acc[normalized] = [];
        acc[normalized].push(item);
      }
      return acc;
    }, {} as Record<string, SavedItem[]>);
  }, [items]);

  // Group items by source
  const sourceGroups = useMemo(() => {
    return items.reduce((acc, item) => {
      let source: string;
      if (item.source_type === 'image') {
        source = 'uploads';
      } else if (item.source_type === 'barcode') {
        source = 'scanned';
      } else {
        const platform = item.platform?.toLowerCase() || detectPlatformFromUrl(item.url);
        if (platform && socialPlatforms.includes(platform)) {
          source = 'social';
        } else {
          source = 'shopping';
        }
      }
      if (!acc[source]) acc[source] = [];
      acc[source].push(item);
      return acc;
    }, {} as Record<string, SavedItem[]>);
  }, [items]);

  // Sorted arrays
  const sortedCategories = useMemo(() =>
    Object.entries(autoCategorizedGroups).sort(([, a], [, b]) => b.length - a.length),
    [autoCategorizedGroups]
  );

  const sortedRetailers = useMemo(() =>
    Object.entries(retailerGroups).sort(([, a], [, b]) => b.length - a.length),
    [retailerGroups]
  );

  const sortedSources = useMemo(() =>
    Object.entries(sourceGroups).sort(([, a], [, b]) => b.length - a.length),
    [sourceGroups]
  );

  // Filter by search query
  const lowerQuery = searchQuery.toLowerCase().trim();
  
  const filteredCategories = useMemo(() => {
    if (!lowerQuery) return sortedCategories;
    return sortedCategories.filter(([cat]) => cat.toLowerCase().includes(lowerQuery));
  }, [sortedCategories, lowerQuery]);

  const filteredRetailers = useMemo(() => {
    if (!lowerQuery) return sortedRetailers;
    return sortedRetailers.filter(([retailer]) => retailer.toLowerCase().includes(lowerQuery));
  }, [sortedRetailers, lowerQuery]);

  const filteredSources = useMemo(() => {
    if (!lowerQuery) return sortedSources;
    return sortedSources.filter(([src]) => {
      const label = sourceConfig[src]?.label || src;
      return label.toLowerCase().includes(lowerQuery);
    });
  }, [sortedSources, lowerQuery]);

  const handleSelect = (filter: string) => {
    // Toggle: if already selected, deselect back to 'all'
    onFilterChange(currentFilter === filter && filter !== 'all' ? 'all' : filter);
    onOpenChange(false);
  };
  
  const handleApplySemanticSearch = () => {
    if (matches.length > 0) {
      const ids = matches.map(m => m.id).join(',');
      lastAppliedQueryRef.current = searchQuery;
      onFilterChange(`search:${ids}`);
      onOpenChange(false);
    }
  };

  const isSelected = (filter: string) => currentFilter === filter;

  const hasResults = filteredCategories.length > 0 || filteredRetailers.length > 0 || filteredSources.length > 0 || matches.length > 0;

  return (
    <DraggableBottomSheet
      open={open}
      onOpenChange={onOpenChange}
      height="90vh"
      showCloseButton={false}
      customHeader={
        <div className="px-4 pb-4 pt-1">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Smart Filters</h2>
                <p className="text-xs text-muted-foreground">Auto-organized by AI</p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-2 rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            {isSearching ? (
              <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary animate-spin" />
            ) : (
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            )}
            <Input
              placeholder="Search anything... e.g. 'trainers', 'blue dress'"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-10 bg-muted/50 border-0 rounded-xl"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  clearSearch();
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-muted"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>
          
          {/* Semantic search hint */}
          {searchQuery.length >= 2 && !isSearching && matches.length === 0 && !error && (
            <p className="text-xs text-muted-foreground mt-2 px-1">
              AI is finding items that match "{searchQuery}"...
            </p>
          )}
        </div>
      }
    >
      <div className="px-4 pb-8 space-y-6">
        {/* Semantic Search Results */}
        {matches.length > 0 && (
          <section>
            <button
              onClick={() => setMatchingItemsExpanded(!matchingItemsExpanded)}
              className="flex items-center justify-between w-full mb-3"
            >
              <div className="flex items-center gap-2">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  AI Matches
                </h3>
                <span className="px-1.5 py-0.5 rounded-full bg-primary/15 text-[10px] font-medium text-primary">
                  {matches.length}
                </span>
              </div>
              {matchingItemsExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            
            {matchingItemsExpanded && (
              <div className="space-y-2">
                {/* Apply all matches button */}
                <button
                  onClick={handleApplySemanticSearch}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-primary text-primary-foreground transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    <span className="font-semibold">Show all matches</span>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-foreground/20">
                    {matches.length} items
                  </span>
                </button>
                
                {/* Individual match previews */}
                <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto">
                  {matches.slice(0, 5).map((match) => {
                    const item = items.find(i => i.id === match.id);
                    if (!item) return null;
                    return (
                      <div
                        key={match.id}
                        className="flex items-center gap-3 p-2 rounded-xl bg-surface-2"
                      >
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt=""
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {item.name || 'Untitled item'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {match.reason}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {match.score}%
                        </span>
                      </div>
                    );
                  })}
                  {matches.length > 5 && (
                    <p className="text-xs text-muted-foreground text-center py-1">
                      +{matches.length - 5} more matches
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Controls Row: Hide Organised + View Picker */}
        <div className="flex items-center gap-2">
          {organisedCount > 0 && (
            <button
              onClick={() => onHideOrganisedChange(!hideOrganised)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors",
                hideOrganised
                  ? "bg-primary text-primary-foreground"
                  : "bg-surface-2 text-muted-foreground hover:bg-surface-3"
              )}
            >
              {hideOrganised ? `Hiding ${organisedCount} items` : `Hide organised`}
              {!hideOrganised && (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  {organisedCount}
                </span>
              )}
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={() => onViewModeChange('grid')}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors",
              viewMode === 'grid'
                ? "bg-surface-3 text-foreground"
                : "bg-surface-2 text-muted-foreground hover:bg-surface-3"
            )}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            Grid
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors",
              viewMode === 'list'
                ? "bg-surface-3 text-foreground"
                : "bg-surface-2 text-muted-foreground hover:bg-surface-3"
            )}
          >
            <List className="w-3.5 h-3.5" />
            List
          </button>
        </div>

        {/* Categories Section */}
        {(filteredCategories.length > 0 || (!lowerQuery || 'all items'.includes(lowerQuery))) && (
          <section>
            <button
              onClick={() => setCategoriesExpanded(!categoriesExpanded)}
              className="flex items-center justify-between w-full mb-3"
            >
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Categories
              </h3>
              {categoriesExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            
            {categoriesExpanded && (
              <div className="grid grid-cols-2 gap-2">
                {/* All Items - full width */}
                {(!lowerQuery || 'all items'.includes(lowerQuery)) && (
                  <button
                    onClick={() => handleSelect('all')}
                    className={cn(
                      "col-span-2 flex items-center gap-2 p-3 mb-3 rounded-2xl transition-colors",
                      isSelected('all')
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface-2 hover:bg-surface-3 text-foreground"
                    )}
                  >
                    <span className="text-lg">✨</span>
                    <span className="text-sm font-semibold flex-1 text-left">All Items</span>
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded-full",
                      isSelected('all')
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {items.length}
                    </span>
                  </button>
                )}
                {(showAllCategories ? filteredCategories : filteredCategories.slice(0, 10)).map(([category, categoryItems]) => (
                  <button
                    key={category}
                    onClick={() => handleSelect(`category:${category}`)}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-2xl transition-colors",
                      isSelected(`category:${category}`)
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface-2 hover:bg-surface-3 text-foreground"
                    )}
                  >
                    <span className="text-lg">{categoryIcons[category] || '📦'}</span>
                    <span className="capitalize text-sm font-medium truncate flex-1 text-left">
                      {category}
                    </span>
                    <span className={cn(
                      "text-xs font-medium px-2 py-0.5 rounded-full",
                      isSelected(`category:${category}`)
                        ? "bg-primary-foreground/20 text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    )}>
                      {categoryItems.length}
                    </span>
                  </button>
                ))}
                {filteredCategories.length > 10 && (
                  <button
                    onClick={() => setShowAllCategories(!showAllCategories)}
                    className="col-span-2 text-sm font-medium text-primary py-2 transition-colors"
                  >
                    {showAllCategories ? 'Show less' : `Show all ${filteredCategories.length} categories`}
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {/* Retailers Section */}
        {filteredRetailers.length > 0 && (
          <section>
            <button
              onClick={() => setRetailersExpanded(!retailersExpanded)}
              className="flex items-center justify-between w-full mb-3"
            >
              <div className="flex items-center gap-2">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                  Retailers
                </h3>
                <span className="px-1.5 py-0.5 rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                  {sortedRetailers.length}
                </span>
              </div>
              {retailersExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            
            {retailersExpanded && (
              <div className="flex flex-wrap gap-2">
                {(showAllRetailers ? filteredRetailers : filteredRetailers.slice(0, 10)).map(([retailer, retailerItems]) => (
                  <button
                    key={retailer}
                    onClick={() => handleSelect(`retailer:${retailer}`)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors",
                      isSelected(`retailer:${retailer}`)
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface-2 hover:bg-surface-3 text-foreground"
                    )}
                  >
                    <SourceIcon url={retailerItems[0]?.url} size="sm" />
                    <span className="text-sm font-medium">{retailer}</span>
                    <span className={cn(
                      "text-xs",
                      isSelected(`retailer:${retailer}`)
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    )}>
                      ({retailerItems.length})
                    </span>
                  </button>
                ))}
                {filteredRetailers.length > 10 && (
                  <button
                    onClick={() => setShowAllRetailers(!showAllRetailers)}
                    className="w-full text-sm font-medium text-primary py-2 transition-colors"
                  >
                    {showAllRetailers ? 'Show less' : `Show all ${filteredRetailers.length} retailers`}
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {/* Sources Section */}
        {filteredSources.length > 0 && (
          <section>
            <button
              onClick={() => setSourcesExpanded(!sourcesExpanded)}
              className="flex items-center justify-between w-full mb-3"
            >
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Sources
              </h3>
              {sourcesExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
            
            {sourcesExpanded && (
              <div className="flex flex-wrap gap-2">
                {filteredSources.map(([source, sourceItems]) => {
                  const config = sourceConfig[source];
                  return (
                    <button
                      key={source}
                      onClick={() => handleSelect(`source:${source}`)}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors",
                        isSelected(`source:${source}`)
                          ? "bg-primary text-primary-foreground"
                          : "bg-surface-2 hover:bg-surface-3 text-foreground"
                      )}
                    >
                      <span className="text-sm font-medium">{config?.label || source}</span>
                      <span className={cn(
                        "text-xs",
                        isSelected(`source:${source}`)
                          ? "text-primary-foreground/70"
                          : "text-muted-foreground"
                      )}>
                        ({sourceItems.length})
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* No results */}
        {lowerQuery && !hasResults && (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No filters match "{searchQuery}"</p>
          </div>
        )}
      </div>
    </DraggableBottomSheet>
  );
}
